import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Box,
} from '@mui/material';
import { Section } from 'shared-components';

/**
 * PricingTable - A comparison table component for displaying pricing tiers and features
 * 
 * Displays a responsive table showing different pricing tiers (columns) and their features (rows).
 * Features can be marked as included (X), excluded (-), or have custom text values.
 * 
 * @param {Object} props - Component props
 * @param {string} [props.title] - Optional title displayed above the table
 * @param {Array<Object>} props.tiers - Array of pricing tier objects
 * @param {string} props.tiers[].name - Name of the tier (e.g., "Free", "Premium")
 * @param {string} props.tiers[].price - Price display text (e.g., "Free", "2.000,-€")
 * @param {Array<Object>} props.features - Array of feature objects to compare across tiers
 * @param {string} props.features[].name - Feature name/description
 * @param {Array<string|boolean>} props.features[].values - Array of values for each tier (true for X, false for -, or custom text)
 * @param {string} [props.footnote] - Optional footnote text displayed below the table
 */
export default function PricingTable({ title, tiers, features, footnote }) {
  return (
    <Section>
      {title && (
        <Typography variant="h2" gutterBottom sx={{ mb: 4 }}>
          {title}
        </Typography>
      )}
      <TableContainer component={Paper} sx={{ mb: footnote ? 2 : 0 }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', minWidth: '200px' }}>
                {/* Empty cell for feature names column */}
              </TableCell>
              {tiers.map((tier, index) => (
                <TableCell
                  key={index}
                  align="center"
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: 'background.default',
                  }}
                >
                  <Typography variant="h6">{tier.name}</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {features.map((feature, featureIndex) => (
              <TableRow
                key={featureIndex}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  '&:nth-of-type(odd)': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <TableCell component="th" scope="row">
                  {feature.name}
                </TableCell>
                {feature.values.map((value, valueIndex) => (
                  <TableCell key={valueIndex} align="center">
                    {typeof value === 'boolean' ? (
                      <Typography sx={{ fontWeight: value ? 'bold' : 'normal' }}>
                        {value ? 'X' : '-'}
                      </Typography>
                    ) : (
                      <Typography>{value}</Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {footnote && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {footnote}
          </Typography>
        </Box>
      )}
    </Section>
  );
}
